<template>
  <div class="inventory-ledger">
    <div class="ledger-filters">
      <div v-if="!fixedSupplyId" class="form-group ledger-filter-field">
        <label :for="supplyFilterId">Insumo</label>
        <v-select
          :id="supplyFilterId"
          :options="supplyFilterOptions"
          label="label"
          :reduce="(option) => option.value"
          v-model="selectedSupplyId"
          placeholder="Todos os insumos"
          :clearable="false"
        />
      </div>
      <div class="form-group ledger-filter-field">
        <label :for="typeFilterId">Tipo</label>
        <v-select
          :id="typeFilterId"
          :options="INVENTORY_TX_TYPE_OPTIONS"
          label="label"
          :reduce="(option) => option.value"
          v-model="selectedType"
          placeholder="Todos os tipos"
          :clearable="false"
        />
      </div>
      <label v-if="!fixedSupplyId" class="ledger-filter-checkbox checkbox-label">
        <input v-model="includeArchivedSupplies" type="checkbox" />
        Incluir insumos arquivados
      </label>
    </div>

    <p v-if="loading" class="ledger-status">Carregando histórico…</p>
    <p v-else-if="loadError" class="field-error" role="alert">{{ loadError }}</p>
    <p v-else-if="displayedRows.length === 0" class="empty-hint">
      Nenhuma movimentação encontrada.
    </p>

    <ul v-else class="ledger-list">
      <li v-for="tx in displayedRows" :key="tx.id" class="ledger-item">
        <div class="ledger-item-main">
          <div class="ledger-item-head">
            <span class="ledger-type">{{ inventoryTransactionTypeLabel(tx.type) }}</span>
            <time class="ledger-when">{{ formatInventoryTransactionWhen(tx.createdAt) }}</time>
          </div>
          <p v-if="showSupplyColumn" class="ledger-supply">{{ supplyName(tx.supplyId) }}</p>
          <p class="ledger-delta">{{ formatInventoryTransactionDelta(tx, supplyById(tx.supplyId)) }}</p>
          <p v-if="inventoryTransactionDetail(tx)" class="ledger-detail">
            {{ inventoryTransactionDetail(tx) }}
          </p>
        </div>
      </li>
    </ul>

    <button
      v-if="hasMore && !loading"
      type="button"
      class="btn btn-secondary ledger-load-more"
      :disabled="loadingMore"
      @click="fetchPage(false)"
    >
      {{ loadingMore ? 'Carregando…' : 'Carregar mais' }}
    </button>
  </div>
</template>

<script>
import { computed, markRaw, onMounted, ref, shallowRef, watch } from 'vue';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import { firestore } from '@/firebase';
import { useSupplyStore } from '@/stores/supply';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import {
  INVENTORY_TX_TYPE_OPTIONS,
  formatInventoryTransactionDelta,
  formatInventoryTransactionWhen,
  inventoryTransactionDetail,
  inventoryTransactionTypeLabel,
} from '@/utils/inventoryTransactionDisplay';

const PAGE_SIZE = 50;

let filterIdCounter = 0;

export default {
  name: 'InventoryLedgerPanel',
  components: { vSelect },
  props: {
    fixedSupplyId: { type: String, default: null },
    showSupplyColumn: { type: Boolean, default: false },
  },
  setup(props) {
    const supplyStore = useSupplyStore();
    const filterIdSuffix = `ledger-${++filterIdCounter}`;
    const supplyFilterId = `ledger-supply-${filterIdSuffix}`;
    const typeFilterId = `ledger-type-${filterIdSuffix}`;

    const selectedSupplyId = ref(null);
    const selectedType = ref('');
    const includeArchivedSupplies = ref(false);
    const rows = ref([]);
    const loading = ref(false);
    const loadingMore = ref(false);
    const hasMore = ref(false);
    const lastDoc = shallowRef(null);
    const loadError = ref('');

    const effectiveSupplyId = computed(() => props.fixedSupplyId || selectedSupplyId.value || null);

    const supplyFilterOptions = computed(() => {
      const supplies = includeArchivedSupplies.value
        ? supplyStore.allSupplies
        : supplyStore.activeSupplies;
      return [
        { value: null, label: 'Todos os insumos' },
        ...supplies.map((supply) => ({
          value: supply.id,
          label: supply.archived ? `${supply.name} (arquivado)` : supply.name,
        })),
      ];
    });

    function isArchivedSupplyTransaction(tx) {
      return !!supplyStore.supplyById(tx.supplyId)?.archived;
    }

    const displayedRows = computed(() => {
      let filtered = rows.value;
      if (!includeArchivedSupplies.value && !props.fixedSupplyId) {
        filtered = filtered.filter((tx) => !isArchivedSupplyTransaction(tx));
      }
      if (selectedType.value) {
        filtered = filtered.filter((tx) => tx.type === selectedType.value);
      }
      return filtered;
    });

    function supplyById(id) {
      return supplyStore.supplyById(id);
    }

    function supplyName(id) {
      return supplyStore.supplyById(id)?.name || id || '—';
    }

    function mapDoc(docSnap) {
      return { id: docSnap.id, ...docSnap.data() };
    }

    async function fetchPage(reset = true) {
      if (reset) {
        loading.value = true;
        lastDoc.value = null;
        rows.value = [];
      } else {
        loadingMore.value = true;
      }
      loadError.value = '';
      try {
        const constraints = [];
        if (effectiveSupplyId.value) {
          constraints.push(where('supplyId', '==', effectiveSupplyId.value));
        }
        constraints.push(orderBy('createdAt', 'desc'));
        if (!reset && lastDoc.value) {
          constraints.push(startAfter(lastDoc.value));
        }
        constraints.push(limit(PAGE_SIZE));
        const snap = await getDocs(query(collection(firestore, 'inventoryTransactions'), ...constraints));
        const items = snap.docs.map(mapDoc);
        rows.value = reset ? items : [...rows.value, ...items];
        lastDoc.value = snap.docs.length > 0 ? markRaw(snap.docs[snap.docs.length - 1]) : null;
        hasMore.value = snap.docs.length === PAGE_SIZE;
      } catch (err) {
        console.error('InventoryLedgerPanel fetch failed', err);
        loadError.value = 'Não foi possível carregar o histórico.';
      } finally {
        loading.value = false;
        loadingMore.value = false;
      }
    }

    watch(effectiveSupplyId, () => {
      fetchPage(true);
    });

    watch(includeArchivedSupplies, (includeArchived) => {
      if (includeArchived) return;
      const selected = selectedSupplyId.value;
      if (selected && supplyStore.supplyById(selected)?.archived) {
        selectedSupplyId.value = null;
      }
    });

    onMounted(() => {
      fetchPage(true);
    });

    return {
      supplyFilterId,
      typeFilterId,
      selectedSupplyId,
      selectedType,
      includeArchivedSupplies,
      supplyFilterOptions,
      INVENTORY_TX_TYPE_OPTIONS,
      displayedRows,
      loading,
      loadingMore,
      hasMore,
      loadError,
      fetchPage,
      supplyName,
      supplyById,
      inventoryTransactionTypeLabel,
      formatInventoryTransactionWhen,
      formatInventoryTransactionDelta,
      inventoryTransactionDetail,
    };
  },
};
</script>

<style scoped>
.inventory-ledger {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
}

.ledger-filters {
  display: grid;
  gap: var(--space-sm);
}

.ledger-filter-field {
  margin: 0;
}

.ledger-filter-field label {
  display: block;
  margin-bottom: var(--space-xs);
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-text-secondary);
}

.ledger-filter-checkbox {
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  user-select: none;
}

.ledger-status,
.empty-hint {
  margin: 0;
  text-align: center;
  color: var(--color-text-muted);
}

.field-error {
  font-size: 0.875rem;
  color: var(--color-error);
  margin: 0;
}

.ledger-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.ledger-item {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-md);
}

.ledger-item-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-sm);
}

.ledger-type {
  font-weight: 700;
  color: var(--color-text);
}

.ledger-when {
  font-size: 0.8rem;
  color: var(--color-text-muted);
  white-space: nowrap;
}

.ledger-supply {
  margin: var(--space-xs) 0 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-accent);
}

.ledger-delta {
  margin: var(--space-xs) 0 0;
  font-size: 0.95rem;
  color: var(--color-text);
}

.ledger-detail {
  margin: var(--space-xs) 0 0;
  font-size: 0.82rem;
  color: var(--color-text-secondary);
}

.ledger-load-more {
  align-self: center;
}
</style>
